import { Component, input, viewChild, signal, effect, ViewEncapsulation } from "@angular/core";
import { HlmSheet, HlmSheetContent } from "@o2-fe/ui";
import { BrnSheetContent } from "@spartan-ng/brain/sheet";
import type { ClassValue } from "clsx";

export type SheetSide = "top" | "bottom" | "left" | "right";

/**
 * O2 Sheet Component
 *
 * Wrapper component for hlm-sheet that provides a consistent layout
 * with header, body, and footer sections. Handles scrolling and styling
 * automatically.
 *
 * Features:
 * - Automatic layout with header, body, footer
 * - Scrollable body with hidden scrollbar
 * - Fixed header and footer
 * - Configurable side (left, right, top, bottom)
 * - Max width settings
 *
 * Usage:
 * <o2-sheet side="right" maxWidth="37.5rem">
 *   <o2-sheet-header>
 *     <h2>Title</h2>
 *   </o2-sheet-header>
 *   <o2-sheet-body>
 *     <!-- Content -->
 *   </o2-sheet-body>
 *   <o2-sheet-footer>
 *     <button>Close</button>
 *   </o2-sheet-footer>
 * </o2-sheet>
 */
@Component({
  selector: "o2-sheet",
  standalone: true,
  imports: [HlmSheet, HlmSheetContent, BrnSheetContent],
  template: `
    <hlm-sheet [side]="side()">
      <hlm-sheet-content
        *brnSheetContent
        [class]="contentClass()"
        [style.max-width]="computedMaxWidth()"
      >
        <ng-content />
      </hlm-sheet-content>
    </hlm-sheet>
  `,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class O2SheetComponent {
  /**
   * ViewChild reference to the underlying HlmSheet
   * Used to expose open/close methods
   */
  private readonly _sheetRef = viewChild.required(HlmSheet);

  /**
   * Signal to track if the sheet is currently open
   * Used by child components (like accordion) to react to sheet state
   */
  public readonly isOpen = signal<boolean>(false);

  constructor() {
    // Effect to sync isOpen signal with underlying sheet state
    effect(() => {
      const sheetRef = this._sheetRef();
      const state = sheetRef.stateComputed();

      // Update our isOpen signal based on sheet state
      this.isOpen.set(state === 'open');
    });
  }

  /**
   * Side where the sheet appears from
   * Default: 'right'
   */
  readonly side = input<SheetSide>("right");

  /**
   * Maximum width for the sheet (for right/left side)
   * Default: '40rem' (640px)
   */
  readonly maxWidth = input<string>("40rem");

  /**
   * Additional CSS classes for the sheet content
   */
  readonly userClass = input<ClassValue>("", { alias: "class" });

  /**
   * Computed class for the sheet content
   */
  protected contentClass(): string {
    const classes = [
      "p-5",
      "flex",
      "flex-col",
      "gap-2.5",
      "h-full",
      "bg-accent",
    ];

    // Add user classes
    const userClass = this.userClass();
    if (userClass) {
      classes.push(String(userClass));
    }

    return classes.join(" ");
  }

  /**
   * Computed max-width style
   * Only applied for right/left sheets
   */
  protected computedMaxWidth(): string | null {
    const side = this.side();
    if (side === "right" || side === "left") {
      return this.maxWidth();
    }
    return null;
  }

  /**
   * Opens the sheet
   * Delegates to the underlying HlmSheet component
   * Note: isOpen signal is automatically updated via effect watching stateComputed
   */
  public open(): void {
    this._sheetRef().open();
  }

  /**
   * Closes the sheet
   * Delegates to the underlying HlmSheet component
   * Note: isOpen signal is automatically updated via effect watching stateComputed
   */
  public close(): void {
    this._sheetRef().close();
  }
}
